import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { format } from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import {
  ChartColumn,
  ChartHeader,
  ChartContainer,
  ContentLayout,
  ContainerWrapper,
  HeaderBar,
  HeaderDivider,
  HeaderLeft,
  HeaderRight,
  HeaderStat,
  HeaderStatLabel,
  HeaderStats,
  HeaderStatValue,
  PriceWrapper,
  SectionLabel,
  SectionStack,
  SwapContainer,
  TickerLogoPair,
  TickerLogoFirst,
  TickerLogoSecond,
  TickerNetworkName,
  TickerPairName,
  TickerSelector,
  TickerTextColumn,
  TierDot,
  TierValue,
  FlipButton,
} from './style'
import { Chevron } from '../../icons/Chevron'
import { PrimaryButton } from '../../components/Button'
import AssetInput from './AssetInput'
import FeeValues from './FeeValues'
import { SegmentedControl } from '../../components/SegmentedControl'
import LineChart, { ChartPoint } from '../../components/LineChart'
import Table from '../../components/Table'
import Dropdown from '../../components/Dropdown'
import MarketDropdownContent from './MarketDropdownContent'
import { balanceForAsset, buildTransactionRow, CHART_RANGE_CONFIG, ChartRangeTab, classifyPairTier, DEFAULT_FROM_PAIR, DEFAULT_TO_PAIR, fetchSwapMarkets, getInstId, getTickerPriority, isSwapForPair, parseCandle, SwapAssetRef, SwapMarket } from './convert-utils'
import LoadingBar from '../../components/LoadingBar'
import { getMethod } from '../../util/Managed'
import { debounce } from 'lodash'
import Radom, { Transaction, TransactionKind, TransactionStatus } from '../../state/Radom'
import { errorToast, formatCurrency, roundAssetAmount, successToast } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import { observer } from 'mobx-react'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import ConfirmModal from './ConfirmModal'

const FlipArrow = (): JSX.Element => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
    <path d='M8 2.5v11M3.5 9l4.5 4.5L12.5 9' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

const Convert = observer((): JSX.Element => {
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const isUnifiedTreasury = User.organization?.features?.isUnifiedTreasuryEnabled === true
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedFromTicker = searchParams.get('from')?.toUpperCase()
  const requestedToTicker = searchParams.get('to')?.toUpperCase()
  const [isUrlPairResolved, setIsUrlPairResolved] = useState(!requestedFromTicker)
  const quoteSeq = useRef(0)

  const [selectedTab, setSelectedTab] = useState<ChartRangeTab>('1D')
  const [fromAsset, setFromAsset] = useState<SwapAssetRef>(DEFAULT_FROM_PAIR)
  const [toAsset, setToAsset] = useState<SwapAssetRef>(DEFAULT_TO_PAIR)
  const [fromInputValue, setFromInputValue] = useState('')
  const [toInputValue, setToInputValue] = useState('')
  const [historicalData, setHistoricalData] = useState<ChartPoint[]>([])
  const [isLoadingChartData, setIsLoadingChartData] = useState<boolean>(true)
  const [isLoadingRate, setIsLoadingRate] = useState<boolean>(false)
  const [fee, setFee] = useState<number | null>(null)
  const [swapTransaction, setSwapTransaction] = useState<Transaction[] | null>(null)
  const [isLoadingTransaction, setIsLoadingTransaction] = useState<boolean>(true)
  const [markets, setMarkets] = useState<SwapMarket[]>([])
  const [isLoadingMarkets, setIsLoadingMarkets] = useState<boolean>(true)
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  const [isConfirming, setIsConfirming] = useState<boolean>(false)

  const fromAmount = Number(fromInputValue) || 0
  const toAmount = Number(toInputValue) || 0

  const exchangeRate = useMemo(() => {
    if (!fromAmount || !toAmount) return 0
    return toAmount / fromAmount
  }, [fromAmount, toAmount])

  const [priceChangePercent, priceMove] = useMemo(() => {
    const firstPrice = historicalData[0]?.y
    const lastPrice = historicalData[historicalData.length - 1]?.y
    if (!firstPrice || !lastPrice) return [0, 0]
    return [((lastPrice - firstPrice) / firstPrice) * 100, lastPrice - firstPrice]
  }, [historicalData])

  const [fromTicker, fromLogo] = useMemo(() => {
    const method = getMethod(fromAsset.network, fromAsset?.token ?? undefined)
    return [method.ticker, method.logo]
  }, [fromAsset])

  const [toTicker, toLogo] = useMemo(() => {
    const method = getMethod(toAsset.network, toAsset?.token ?? undefined)
    return [method.ticker, method.logo]
  }, [toAsset])

  const [primaryPair, secondaryPair] = useMemo(() => {
    const from = { ticker: fromTicker, logo: fromLogo, label: fromAsset.network }
    const to = { ticker: toTicker, logo: toLogo, label: toAsset.network }
    return [from, to].sort((a, b) => getTickerPriority(a.ticker) - getTickerPriority(b.ticker))
  }, [fromAsset, toAsset, fromTicker, fromLogo, toTicker, toLogo])

  const fromBalance = useMemo(
    () => balanceForAsset(Treasury.managedBalances, fromAsset, fromTicker),
    [Treasury.managedBalances, fromAsset, fromTicker]
  )

  const toBalance = useMemo(
    () => balanceForAsset(Treasury.managedBalances, toAsset, toTicker),
    [Treasury.managedBalances, toAsset, toTicker]
  )

  const instId = useMemo(() => getInstId(fromAsset, toAsset), [fromAsset, toAsset])

  const price = useMemo(() => {
    const lastPrice = historicalData[historicalData.length - 1]?.y
    return lastPrice ? `${formatCurrency(lastPrice, 'USD')}` : '$0.00'
  }, [historicalData])

  const [rangeHigh, rangeLow] = useMemo<[number | null, number | null]>(() => {
    if (historicalData.length === 0) return [null, null]
    const values = historicalData.map(d => d.y)
    return [Math.max(...values), Math.min(...values)]
  }, [historicalData])

  const pairTransactions = useMemo(
    () => swapTransaction?.filter(tx => isSwapForPair(tx, fromTicker, toTicker)) ?? [],
    [swapTransaction, fromTicker, toTicker]
  )

  const pairTier = useMemo(
    () => classifyPairTier(primaryPair.ticker, secondaryPair.ticker),
    [primaryPair.ticker, secondaryPair.ticker]
  )

  const isOverBalance = fromAmount > fromBalance

  const isSwapDisabled = useMemo(() => {
    if (fromAmount <= 0 || toAmount <= 0) return true
    if (isOverBalance) return true
    return false
  }, [fromAmount, toAmount, isOverBalance])

  const formatXAxis = useCallback((v: number): string => {
    if (selectedTab === '1D') return format(new Date(v), 'HH:mm')
    return format(new Date(v), 'MMM d')
  }, [selectedTab])

  const formatTooltipX = useCallback((v: number): string => {
    if (selectedTab === '1D' || selectedTab === '1W') return format(new Date(v), 'MMM d, HH:mm')
    return format(new Date(v), 'MMM d, yyyy')
  }, [selectedTab])

  const fetchSwapTransaction = useCallback(async (): Promise<void> => {
    setIsLoadingTransaction(true)
    try {
      const response = await Radom.listTransactions(0, 100, [TransactionKind.Swap],
        [TransactionStatus.Failed,
          TransactionStatus.Incomplete,
          TransactionStatus.Pending,
          TransactionStatus.Successful],
        [fromAsset.network, toAsset.network],
        new Date(0).toISOString(),
        new Date().toISOString(),
        false
      )
      setSwapTransaction(response.data)
    } catch (error) {
      console.error('Error fetching swap transactions:', error)
    } finally {
      setIsLoadingTransaction(false)
    }
  }, [fromAsset, toAsset])

  const fetchSwapRate = useCallback(async (value: number): Promise<void> => {
    if (!value) {
      quoteSeq.current += 1
      setToInputValue('')
      return
    }
    const seq = ++quoteSeq.current
    setIsLoadingRate(true)
    try {
      const { amount, fee } = await Radom.getSwapRates({
        from: fromAsset as { network: string, token?: string },
        into: toAsset as { network: string, token?: string },
        amount: value
      })
      if (seq !== quoteSeq.current) return
      setFee(+fee)
      setToInputValue(String(amount))
    } catch (error) {
      console.error('Error fetching swap rate:', error)
    } finally {
      if (seq === quoteSeq.current) {
        setIsLoadingRate(false)
      }
    }
  }, [fromAsset, toAsset])

  const debouncedFetchSwapRate = useMemo(() => debounce(fetchSwapRate, 300), [fetchSwapRate])

  useEffect(() => {
    return () => debouncedFetchSwapRate.cancel()
  }, [debouncedFetchSwapRate])

  useEffect(() => {
    if (!User.organization) return

    const loadMarkets = async (): Promise<void> => {
      try {
        setMarkets(await fetchSwapMarkets(isUnifiedTreasury))
      } catch (error) {
        console.error('Error fetching swap markets:', error)
      } finally {
        setIsLoadingMarkets(false)
      }
    }
    loadMarkets()
  }, [isUnifiedTreasury, User.organization])

  useEffect(() => {
    if (!permissionsLoaded || !has('ReadBalance')) return
    void Treasury.loadBalances()
    void Treasury.loadPriceQuotes()
  }, [permissionsLoaded])

  useEffect(() => {
    fetchSwapTransaction()
  }, [fetchSwapTransaction])

  useEffect(() => {
    if (isUrlPairResolved || markets.length === 0) return
    setIsUrlPairResolved(true)

    if (!requestedFromTicker) return

    const market = markets.find(m => requestedToTicker
      ? (m.base === requestedFromTicker && m.quote === requestedToTicker) ||
        (m.quote === requestedFromTicker && m.base === requestedToTicker)
      : m.base === requestedFromTicker || m.quote === requestedFromTicker)
    if (!market) return

    const isBase = market.base === requestedFromTicker
    setFromAsset(isBase ? market.baseAsset : market.quoteAsset)
    setToAsset(isBase ? market.quoteAsset : market.baseAsset)
  }, [requestedFromTicker, requestedToTicker, markets, isUrlPairResolved])

  useEffect(() => {
    if (!isUrlPairResolved) return
    if (searchParams.get('from') === fromTicker && searchParams.get('to') === toTicker) return

    const next = new URLSearchParams(searchParams)
    next.set('from', fromTicker)
    next.set('to', toTicker)
    setSearchParams(next, { replace: true })
  }, [fromTicker, toTicker, isUrlPairResolved])

  useEffect(() => {
    const fetchCandles = async (): Promise<void> => {
      setIsLoadingChartData(true)
      try {
        const { bar, limit } = CHART_RANGE_CONFIG[selectedTab]
        const { candles } = await Radom.getSwapCandles(instId, bar, limit)

        setHistoricalData(candles.map(parseCandle).reverse())
      } catch (error) {
        console.error('Error fetching candles:', error)
      } finally {
        setIsLoadingChartData(false)
      }
    }
    fetchCandles()
  }, [instId, selectedTab])

  // Bumping the sequence discards any quote still in flight: it was priced for
  // the pair being left behind, and would otherwise land in the cleared ticket.
  const clearTicket = (): void => {
    quoteSeq.current += 1
    setFromInputValue('')
    setToInputValue('')
    setFee(0)
    setIsLoadingRate(false)
  }

  const handleSwapPairChange = (
    from: { network: string, token?: string },
    to: { network: string, token?: string }): void => {
    setFromAsset(from)
    setToAsset(to)
    clearTicket()
  }
  // The flipper: what was bought is now sold and the other way round. The
  // typed amount does not carry across because it changes unit.
  const flipPair = (): void => {
    const sold = fromAsset
    setFromAsset(toAsset)
    setToAsset(sold)
    clearTicket()
  }

  // The amount's value in the organisation's reporting currency, from the
  // same price feed the balances use.
  const reportingCurrency = User.organization?.defaultCurrency ?? 'USD'
  const fiatValueOf = (ticker: string, amount: string | number): string | undefined => {
    const quantity = Number(amount)
    if (!ticker || !(quantity > 0)) return undefined
    const quote = Treasury.priceQuotes.find(q => q.from.toLowerCase() === ticker.toLowerCase())
    if (!quote || !(quote.value > 0)) return undefined
    return formatCurrency(quantity * quote.value, reportingCurrency)
  }

  const handleFromInputChange = (value: string): void => {
    setFromInputValue(value)
    debouncedFetchSwapRate(Number(value) || 0)
  }

  const handleConfirmSwap = async (): Promise<void> => {
    if (!fromAmount) return
    setIsConfirming(true)
    try {
      await Radom.initiateSwap({
        from: fromAsset as { network: string, token?: string },
        into: toAsset as { network: string, token?: string },
        amount: fromAmount
      })
      successToast('Swap submitted')
      setIsConfirmOpen(false)
      clearTicket()
      Treasury.loadBalances()
      fetchSwapTransaction()
    } catch (error) {
      errorToast((error as { response?: { data?:
      { error?: string } } }).response?.data?.error || (error as Error).message)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <ContainerWrapper>
      <HeaderBar>
        <HeaderLeft>
          <Dropdown
            iconTrigger
            noChevron
            containerStyle={{ width: 'fit-content' }}
            outerStyle={{ padding: '8px 12px', borderRadius: 14, columnGap: 12 }}
            selectedContent={
              <TickerSelector>
                <TickerLogoPair>
                  <TickerLogoFirst src={primaryPair.logo} alt={`${primaryPair.ticker} Logo`} />
                  <TickerLogoSecond src={secondaryPair.logo} alt={`${secondaryPair.ticker} Logo`} />
                </TickerLogoPair>
                <TickerTextColumn>
                  <TickerPairName>
                    {primaryPair.ticker}/{secondaryPair.ticker}
                    <Chevron style={{ opacity: 0.35, minWidth: 9 }} />
                  </TickerPairName>
                  <TickerNetworkName>{primaryPair.label}</TickerNetworkName>
                </TickerTextColumn>
              </TickerSelector>
            }
            dropdownContent={
              <MarketDropdownContent markets={markets} isLoading={isLoadingMarkets} onSelect={handleSwapPairChange}/>
            }
          />

          <HeaderStats>
            <HeaderStat>
              <HeaderStatLabel>Fee Tier</HeaderStatLabel>
              <TierValue $accent={pairTier.accent}>
                <TierDot $accent={pairTier.accent} />
                {pairTier.label}
              </TierValue>
            </HeaderStat>
            <HeaderDivider />
            <HeaderStat>
              <HeaderStatLabel>{selectedTab} High</HeaderStatLabel>
              {isLoadingChartData ? <LoadingBar style={{ height: 19, width: 120 }} /> : <HeaderStatValue $trend="up">{formatCurrency(rangeHigh ?? 0, 'USD')}</HeaderStatValue>}
            </HeaderStat>
            <HeaderDivider />
            <HeaderStat>
              <HeaderStatLabel>{selectedTab} Low</HeaderStatLabel>
              {isLoadingChartData ? <LoadingBar style={{ height: 19, width: 120 }} /> : <HeaderStatValue $trend="down">{formatCurrency(rangeLow ?? 0, 'USD')}</HeaderStatValue>}
            </HeaderStat>
          </HeaderStats>
        </HeaderLeft>

        <HeaderRight />
      </HeaderBar>

      <ContentLayout>
        <ChartColumn>
          <ChartContainer>
            <ChartHeader>
              <PriceWrapper $positive={priceMove >= 0}>
                <span>{price}</span>
                {isLoadingChartData
                  ? <LoadingBar style={{ height: 21, width: 200 }}/>
                  : <span>{formatCurrency(priceMove, 'USD')} ({priceChangePercent.toFixed(2)}%) {selectedTab === '1D' ? '24H' : selectedTab}</span>}
              </PriceWrapper>
              <SegmentedControl
                onTabChange={(tab) => setSelectedTab(tab as ChartRangeTab)}
                selectedTab={selectedTab}
                tabs={['1D', '1W', '1M', '3M', '1Y']}
              />
            </ChartHeader>
            <LineChart
              data={historicalData}
              height={360}
              isLoading={isLoadingChartData}
              accentColor={priceMove >= 0 ? 'var(--trend-up, #26a17b)' : 'var(--trend-down, #be3832)'}
              formatXAxis={formatXAxis}
              formatTooltipX={formatTooltipX}
            />
          </ChartContainer>

          <SectionStack>
            <SectionLabel>Recent conversions</SectionLabel>
            <Table
              borderless
              emptyPlaceholderText={`No ${primaryPair.ticker}/${secondaryPair.ticker} conversions yet`}
              isLoading={isLoadingTransaction}
              headers={['Asset', 'Status', 'Exchange Rate', 'Amount', 'Fee', 'Date'].map((header, index) => <span key={index}>{header}</span>)}
              items={pairTransactions.map(buildTransactionRow)}
            />
          </SectionStack>
        </ChartColumn>

        <SwapContainer>
          <AssetInput
            label="Sell"
            balance={fromBalance}
            assetLogo={fromLogo}
            assetSymbol={fromTicker}
            value={fromInputValue}
            fiatValue={fiatValueOf(fromTicker, fromInputValue)}
            onChange={handleFromInputChange} />
          <FlipButton type='button' aria-label='Swap the assets' disabled={isLoadingRate} onClick={flipPair}>
            <FlipArrow />
          </FlipButton>
          <AssetInput
            label="Buy"
            balance={toBalance}
            isLoading={isLoadingRate}
            assetLogo={toLogo}
            assetSymbol={toTicker}
            value={toAmount ? roundAssetAmount(toTicker, toAmount) : ''}
            fiatValue={toAmount ? fiatValueOf(toTicker, toAmount) : undefined}
            onChange={setToInputValue} />
          <FeeValues isLoading={isLoadingRate} exchangeRate={`1 ${fromTicker} = ${roundAssetAmount(toTicker, exchangeRate)} ${toTicker}`} fee={`${roundAssetAmount(fromTicker, fee ?? 0)} ${fromTicker}`} />
          <PrimaryButton
            disabled={isLoadingRate || isSwapDisabled}
            onClick={() => setIsConfirmOpen(true)}
          >
            {isOverBalance ? `Not enough ${fromTicker}` : 'Convert'}
          </PrimaryButton>
        </SwapContainer>
      </ContentLayout>

      {isConfirmOpen &&
        <ConfirmModal
          isConfirmOpen={isConfirmOpen}
          setIsConfirmOpen={setIsConfirmOpen}
          isConfirming={isConfirming}
          handleConfirmSwap={handleConfirmSwap}
          fromLogo={fromLogo}
          fromTicker={fromTicker}
          fromInputValue={fromAmount}
          toLogo={toLogo}
          toTicker={toTicker}
          toInputValue={toAmount}
          exchangeRate={exchangeRate}
          fee={fee}
        />
      }
    </ContainerWrapper>
  )
})

export default Convert